"use client";

import Fuse from "fuse.js";
import { useMemo } from "react";

import { NoArtistInstrumentModelMixedModel } from "@/components/selectArtistModal/NoArtistInstrumentModelMixedModel";
import { NeedPaid } from "@/components/selectArtistModal/savedTab/NeedPaid";
import { VoiceGrid } from "@/components/selectArtistModal/VoiceGrid";
import { artists } from "@/constants/artists/artists";
import { instruments } from "@/constants/instruments";
import { useNamedSelectArtistFilters } from "@/lib/zustand/useSelectArtistFilters";
import {
  ArtistInstumentSaved,
  MixedModelConstructedNoDates,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { isFreeUser } from "@/utils/subscriptionLevels/isFreeUser";

export function SavedTab({
  handleClickArtist,
  closeModal,
  user,
  confirmedDoneModels,
  confirmedDoneMixedModels,
  savedArtistsInstrumentsIds,
  namespace,
}: {
  handleClickArtist: (artistId: string) => void;
  closeModal: () => void;
  user: UserNoDates | null;
  confirmedDoneModels: ModelNoDates[] | null;
  confirmedDoneMixedModels: MixedModelConstructedNoDates[] | null;
  savedArtistsInstrumentsIds: ArtistInstumentSaved["artistInstrumentId"][];
  namespace: string;
}) {
  const useSelectArtistFilters = useNamedSelectArtistFilters((state) =>
    state.getNamedStore(namespace)
  );
  const searchString = useSelectArtistFilters((state) => state.searchString);

  // Filter in saved artists
  const savedArtists = artists.filter((artist) =>
    savedArtistsInstrumentsIds.includes(artist.id)
  );

  // Filter in saved instruments
  const savedInstruments = instruments.filter((instrument) =>
    savedArtistsInstrumentsIds.includes(instrument.id)
  );

  // Filter in saved trained voices
  const savedConfirmedDoneModels =
    confirmedDoneModels === null ?
      []
    : confirmedDoneModels.filter((model) => model.saved);

  // Filter in saved mixed models
  const savedConfirmedDoneMixedModels =
    confirmedDoneMixedModels === null ?
      []
    : confirmedDoneMixedModels.filter((model) => model.saved);

  // Combine all saved
  const savedArtistsInstrumentsModelsMixedModels = [
    ...savedArtists,
    ...savedInstruments,
    ...savedConfirmedDoneModels,
    ...savedConfirmedDoneMixedModels,
  ];

  if (user === null || isFreeUser(user.stripeSubscriptionId)) {
    return <NeedPaid user={user} />;
  }

  const fuse = useMemo(
    () =>
      new Fuse(savedArtistsInstrumentsModelsMixedModels, {
        includeScore: false,
        isCaseSensitive: false,
        keys: ["name"],
        threshold: 0.1,
      }),
    [savedArtistsInstrumentsModelsMixedModels]
  );
  // Filter based on search
  const filteredSaved =
    searchString !== "" ?
      fuse.search(searchString).map((result) => result.item)
    : savedArtistsInstrumentsModelsMixedModels;

  const noSavedVoices = savedArtistsInstrumentsModelsMixedModels.length === 0;
  const noSearchResults = filteredSaved.length === 0;

  if (noSavedVoices) {
    return <NoArtistInstrumentModelMixedModel text="No saved voices." />;
  }

  return noSearchResults ?
      <NoArtistInstrumentModelMixedModel text="No voices found. Try changing your search term." />
    : <VoiceGrid
        voices={filteredSaved}
        handleClickArtist={(artistId) => {
          handleClickArtist(artistId);
          closeModal();
        }}
        user={user}
        savedArtistsInstrumentsIds={savedArtistsInstrumentsIds}
      />;
}
